<template>
<div class="st-page">
  <Navigation />
  <div class="container" style="padding-top:20px;">
    <div class="d-flex align-items-center justify-content-between mb-4">
      <div class="d-flex align-items-center gap-3">
        <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="st-back"><i class="mdi mdi-arrow-left"></i></a>
        <div><h4 class="st-page-title"><i class="mdi mdi-message-text-fast me-2"></i>Bulk SMS & Credits</h4></div>
      </div>
      <button class="st-btn st-btn-blue" @click="showCompose=true"><i class="mdi mdi-plus me-1"></i>New Campaign</button>
    </div>

    <!-- SMS Balance Card -->
    <div class="st-stat-row">
      <div class="st-stat">
        <div class="st-stat-num" :style="{color: balance < 100 ? '#EF4444' : '#4FC3F7'}">{{ balance }}</div>
        <div class="st-stat-lbl">SMS Credits Remaining</div>
      </div>
      <div class="st-stat" @click="showBundles=true" style="cursor:pointer;">
        <div class="st-stat-num" style="color:#10B981;"><i class="mdi mdi-cart-plus"></i></div>
        <div class="st-stat-lbl">Buy SMS Bundle</div>
      </div>
    </div>

    <!-- Tabs -->
    <div class="st-tabs">
      <button class="st-tab" :class="{active:tab==='history'}" @click="tab='history'"><i class="mdi mdi-history me-1"></i>Credit History</button>
      <button class="st-tab" :class="{active:tab==='campaigns'}" @click="tab='campaigns';loadCampaigns()"><i class="mdi mdi-send me-1"></i>Campaigns</button>
    </div>

    <!-- Credit History Tab -->
    <div class="st-card" v-if="tab==='history'">
      <div class="st-card-title">SMS Credit History</div>
      <div v-if="loading" class="st-spinner"><div class="spinner-border"></div></div>
      <div v-else-if="history.length" style="overflow-x:auto;">
        <table class="st-table">
          <thead><tr><th>Date</th><th>Amount</th><th>Source</th><th>Description</th><th>Balance After</th></tr></thead>
          <tbody>
            <tr v-for="h in history" :key="h.id">
              <td>{{ formatDate(h.created_at) }}</td>
              <td>
                <span :style="{color: h.amount > 0 ? '#10B981' : '#EF4444', fontWeight:'700'}">
                  {{ h.amount > 0 ? '+' : '' }}{{ h.amount }}
                </span>
              </td>
              <td><span class="st-badge" :class="sourceBadge(h.source)">{{ h.source }}</span></td>
              <td style="max-width:250px;font-size:12px;">{{ h.description }}</td>
              <td style="font-weight:600;">{{ h.balance_after }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="st-empty"><i class="mdi mdi-message-text"></i>No SMS history yet</div>
    </div>

    <!-- Campaigns Tab -->
    <div class="st-card" v-if="tab==='campaigns'">
      <div class="st-card-title">SMS Campaigns</div>
      <div v-if="campaignsLoading" class="st-spinner"><div class="spinner-border"></div></div>
      <div v-else-if="campaigns.length" style="overflow-x:auto;">
        <table class="st-table">
          <thead><tr><th>Title</th><th>Target</th><th>Recipients</th><th>Delivered</th><th>Failed</th><th>Status</th><th>Date</th></tr></thead>
          <tbody>
            <tr v-for="c in campaigns" :key="c.id">
              <td>{{ c.title || 'Untitled' }}</td>
              <td><span class="st-badge st-badge-info">{{ c.target_type }}</span></td>
              <td>{{ c.recipient_count }}</td>
              <td style="color:#10B981;">{{ c.delivered_count }}</td>
              <td style="color:#EF4444;">{{ c.failed_count }}</td>
              <td><span class="st-badge" :class="{'st-badge-success':c.status==='sent','st-badge-warning':c.status==='scheduled','st-badge-info':c.status==='draft'}">{{ c.status }}</span></td>
              <td>{{ formatDate(c.created_at) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="st-empty"><i class="mdi mdi-send"></i>No campaigns sent yet</div>
    </div>

    <!-- Compose Modal -->
    <div class="st-modal-overlay" v-if="showCompose" @click.self="showCompose=false">
      <div class="st-modal">
        <div class="st-modal-title"><span><i class="mdi mdi-send me-2"></i>New SMS Campaign</span><button class="st-modal-close" @click="showCompose=false">&times;</button></div>
        <div class="st-alert st-alert-danger" v-if="composeError">{{ composeError }}</div>
        <div class="st-alert st-alert-success" v-if="composeSuccess">{{ composeSuccess }}</div>
        <div class="mb-3">
          <label class="st-label">Target Audience</label>
          <select class="st-select" v-model="compose.targetType">
            <option value="">Select target...</option>
            <option value="all_parents">All Parents</option>
            <option value="class">Specific Class</option>
            <option value="campus">Specific Campus</option>
            <option value="staff">Staff Only</option>
          </select>
        </div>
        <div class="mb-3" v-if="compose.targetType==='class'">
          <label class="st-label">Select Class</label>
          <input class="st-input" v-model="compose.targetId" placeholder="Class ID" />
        </div>
        <div class="mb-3">
          <label class="st-label">Campaign Title (optional)</label>
          <input class="st-input" v-model="compose.title" placeholder="e.g. Fee Reminder Term 1" />
        </div>
        <div class="mb-3">
          <label class="st-label">Message</label>
          <textarea class="st-textarea" v-model="compose.message" placeholder="Type your message..." rows="4"></textarea>
          <div style="text-align:right;color:#8BA4C4;font-size:11px;margin-top:4px;">{{ compose.message.length }} chars · {{ Math.ceil(compose.message.length / 160) || 0 }} SMS unit(s)</div>
        </div>
        <div class="mb-3">
          <label class="st-label">Schedule (optional — leave empty to send now)</label>
          <input type="datetime-local" class="st-input" v-model="compose.scheduledAt" />
        </div>
        <div style="background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:12px;margin-bottom:16px;">
          <div style="color:#8BA4C4;font-size:12px;">SMS Balance: <strong :style="{color: balance < 100 ? '#EF4444' : '#4FC3F7'}">{{ balance }}</strong></div>
        </div>
        <button class="btn st-btn-primary w-100" @click="sendCampaign" :disabled="sending">
          <span v-if="sending" class="spinner-border spinner-border-sm me-1"></span>
          {{ compose.scheduledAt ? 'Schedule Campaign' : 'Send Now' }}
        </button>
      </div>
    </div>

    <!-- Buy Bundles Modal -->
    <div class="st-modal-overlay" v-if="showBundles" @click.self="showBundles=false">
      <div class="st-modal">
        <div class="st-modal-title"><span><i class="mdi mdi-cart me-2"></i>Buy SMS Bundle</span><button class="st-modal-close" @click="showBundles=false">&times;</button></div>
        <div v-if="bundlesLoading" class="st-spinner"><div class="spinner-border"></div></div>
        <div v-else>
          <div v-for="b in bundles" :key="b.id" class="st-bundle-card" @click="buyBundle(b)">
            <div class="st-bundle-name">{{ b.name }}</div>
            <div class="st-bundle-count">{{ Number(b.sms_count).toLocaleString() }} SMS</div>
            <div class="st-bundle-price">KSh {{ Number(b.price_ksh).toLocaleString() }}</div>
            <div class="st-bundle-rate">KSh {{ (b.price_ksh / b.sms_count).toFixed(2) }} per SMS</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/simi-auth/Navigation.vue";
import { executeJson, simitrack } from "@/api";
export default {
  name: "BulkSMS",
  components: { Navigation },
  data() {
    return {
      loading: true,
      tab: 'history',
      balance: 0,
      history: [],
      campaigns: [],
      campaignsLoading: false,
      bundles: [],
      bundlesLoading: false,
      showCompose: false,
      showBundles: false,
      compose: { targetType: '', targetId: '', title: '', message: '', scheduledAt: '' },
      sending: false,
      composeError: '',
      composeSuccess: '',
    };
  },
  mounted() { this.loadBalance(); },
  methods: {
    async loadBalance() {
      this.loading = true;
      try {
        var r = await executeJson({ function: 'getSMSBalance' }, simitrack);
        if (r.data.success) {
          this.balance = r.data.data.balance || 0;
          this.history = r.data.data.history || [];
        }
      } catch (e) { console.error(e); }
      this.loading = false;
    },
    async loadCampaigns() {
      this.campaignsLoading = true;
      try {
        var r = await executeJson({ function: 'getBulkSMSCampaigns' }, simitrack);
        if (r.data.success) this.campaigns = r.data.data || [];
      } catch (e) {}
      this.campaignsLoading = false;
    },
    async sendCampaign() {
      this.composeError = ''; this.composeSuccess = '';
      if (!this.compose.targetType || !this.compose.message) { this.composeError = 'Target and message are required'; return; }
      this.sending = true;
      try {
        var r = await executeJson({ function: 'sendBulkSMS', ...this.compose }, simitrack);
        if (r.data.success) {
          this.composeSuccess = r.data.message;
          this.compose = { targetType: '', targetId: '', title: '', message: '', scheduledAt: '' };
          this.loadBalance();
          setTimeout(() => { this.showCompose = false; this.composeSuccess = ''; }, 2000);
        } else { this.composeError = r.data.message || 'Failed'; }
      } catch (e) { this.composeError = 'Connection error'; }
      this.sending = false;
    },
    async buyBundle(bundle) {
      var phone = prompt('Enter M-Pesa phone number to pay KSh ' + Number(bundle.price_ksh).toLocaleString() + ' for ' + Number(bundle.sms_count).toLocaleString() + ' SMS:');
      if (!phone) return;
      try {
        var r = await executeJson({ function: 'buySMSBundle', bundleId: bundle.id, phone: phone }, simitrack);
        if (r.data.success) { alert(r.data.message); this.showBundles = false; }
        else { alert(r.data.message || 'Failed'); }
      } catch (e) { alert('Connection error'); }
    },
    formatDate(dt) {
      if (!dt) return '-';
      var d = new Date(dt);
      return d.toLocaleDateString('en-KE', { day: '2-digit', month: 'short', year: 'numeric' }) + ' ' + d.toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit' });
    },
    sourceBadge(source) {
      if (source === 'registration') return 'st-badge-success';
      if (source === 'purchase') return 'st-badge-info';
      if (source === 'admin_allocation') return 'st-badge-warning';
      if (source === 'deduction') return 'st-badge-danger';
      return 'st-badge-info';
    }
  }
};
</script>

<style scoped>
.st-page{min-height:100vh;background:#0A1628;padding-top:70px;}
.st-back{color:#8BA4C4;font-size:22px;text-decoration:none;padding:4px;}.st-back:hover{color:#4FC3F7;}
.st-page-title{color:#E8EDF4;font-size:18px;font-weight:700;margin:0;}
.st-stat-row{display:flex;flex-wrap:wrap;gap:12px;margin-bottom:20px;}
.st-stat{background:#0F2347;border:1px solid #1A3A6A;border-radius:12px;padding:16px 20px;flex:1;min-width:140px;transition:all .2s;}
.st-stat:hover{border-color:#2E86DE;transform:translateY(-2px);}
.st-stat-num{font-size:24px;font-weight:800;color:#4FC3F7;}
.st-stat-lbl{font-size:12px;color:#8BA4C4;margin-top:2px;}
.st-tabs{display:flex;flex-wrap:wrap;gap:6px;margin-bottom:20px;}
.st-tab{background:#0F2347;border:1px solid #1A3A6A;color:#8BA4C4;border-radius:50px;padding:7px 16px;font-size:13px;font-weight:600;cursor:pointer;transition:all .2s;}
.st-tab.active,.st-tab:hover{background:rgba(46,134,222,.15);border-color:#2E86DE;color:#4FC3F7;}
.st-card{background:#0F2347;border:1px solid #1A3A6A;border-radius:14px;padding:24px;margin-bottom:16px;}
.st-card-title{color:#E8EDF4;font-size:16px;font-weight:700;margin-bottom:16px;}
.st-table{width:100%;border-collapse:collapse;}
.st-table th{color:#8BA4C4;font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:1px;padding:10px 12px;border-bottom:1px solid #1A3A6A;text-align:left;}
.st-table td{color:#E8EDF4;font-size:13px;padding:12px;border-bottom:1px solid #1A3A6A;}
.st-table tr:hover td{background:rgba(46,134,222,.05);}
.st-badge{padding:3px 10px;border-radius:50px;font-size:11px;font-weight:600;}
.st-badge-success{background:rgba(16,185,129,.15);color:#10B981;}
.st-badge-warning{background:rgba(245,158,11,.15);color:#F59E0B;}
.st-badge-danger{background:rgba(239,68,68,.15);color:#EF4444;}
.st-badge-info{background:rgba(46,134,222,.15);color:#4FC3F7;}
.st-btn{border:none;border-radius:8px;padding:8px 16px;font-size:13px;font-weight:600;cursor:pointer;transition:all .2s;}
.st-btn-blue{background:rgba(46,134,222,.15);color:#4FC3F7;border:1px solid rgba(46,134,222,.3);}
.st-btn-blue:hover{background:rgba(46,134,222,.25);}
.st-btn-primary{background:linear-gradient(135deg,#2E86DE,#1A6BC4);color:#fff;border:none;font-weight:600;border-radius:10px;padding:12px;font-size:15px;}
.st-btn-primary:disabled{opacity:.6;}
.st-empty{text-align:center;padding:40px 20px;color:#8BA4C4;}.st-empty i{font-size:48px;color:#1A3A6A;display:block;margin-bottom:12px;}
.st-spinner{display:flex;justify-content:center;padding:40px;}.st-spinner .spinner-border{color:#2E86DE;}
.st-modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,.6);z-index:200;display:flex;align-items:center;justify-content:center;padding:20px;}
.st-modal{background:#0F2347;border:1px solid #1A3A6A;border-radius:16px;padding:28px;width:100%;max-width:500px;max-height:90vh;overflow-y:auto;}
.st-modal-title{color:#E8EDF4;font-size:18px;font-weight:700;margin-bottom:20px;display:flex;align-items:center;justify-content:space-between;}
.st-modal-close{color:#8BA4C4;cursor:pointer;font-size:22px;background:none;border:none;}
.st-label{color:#8BA4C4;font-size:13px;font-weight:600;margin-bottom:6px;display:block;}
.st-input,.st-select,.st-textarea{width:100%;background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:10px 14px;color:#E8EDF4;font-size:14px;outline:none;}
.st-input:focus,.st-select:focus,.st-textarea:focus{border-color:#2E86DE;}
.st-textarea{min-height:80px;resize:vertical;}
.st-alert{padding:10px 14px;border-radius:10px;font-size:13px;margin-bottom:16px;}
.st-alert-danger{background:rgba(255,107,107,.1);border:1px solid rgba(255,107,107,.2);color:#FF9999;}
.st-alert-success{background:rgba(16,185,129,.1);border:1px solid rgba(16,185,129,.2);color:#6EE7B7;}
.st-bundle-card{background:#0A1628;border:1px solid #1A3A6A;border-radius:12px;padding:16px;margin-bottom:10px;cursor:pointer;transition:all .2s;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:8px;}
.st-bundle-card:hover{border-color:#2E86DE;transform:translateY(-2px);}
.st-bundle-name{color:#E8EDF4;font-weight:700;font-size:14px;}
.st-bundle-count{color:#4FC3F7;font-weight:700;font-size:16px;}
.st-bundle-price{color:#10B981;font-weight:700;font-size:14px;}
.st-bundle-rate{color:#8BA4C4;font-size:11px;}
@media(max-width:768px){.st-stat-row{flex-direction:column;}}
</style>
